import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';

import { getClips } from './get-clips';

// The backend builds the report inside the request and refuses more ids than
// this (`ClipSponsorshipReportRequestSerializer.MAX_CLIP_IDS`). Keep them equal.
export const SPONSORSHIP_REPORT_MAX_CLIPS = 500;

export type TPostSponsorshipReport = {
  clip_ids: string[];
  // Pre-fills the report's Link column; `null` leaves it empty.
  workspace_id: string | null;
};

/**
 * The ids of every clip `filters` matches, for a report on "select all": the
 * list only holds the pages loaded so far. The clips list returns no total, so
 * one clip more than the report accepts is asked for, which is enough to tell
 * a filter over the limit apart. The caller rejects that case.
 */
export async function getSponsorshipReportClipIds(
  filters: Partial<ProgramBroadcastFiltersState>
): Promise<string[]> {
  const clips = await getClips({
    filters,
    params: { offset: 0, limit: SPONSORSHIP_REPORT_MAX_CLIPS + 1 },
  });

  return clips.map(clip => clip.id);
}

export async function postSponsorshipReport(body: TPostSponsorshipReport): Promise<Blob> {
  // The response is the .xlsx itself, so it has to be read as a blob. The
  // client's interceptor already unwraps `response.data`.
  //
  // No `format` is asked for: the controller answers with a plain
  // `HttpResponse` holding the workbook, which no renderer touches, so the
  // client's default `Accept: application/json` negotiates fine and keeps
  // error bodies as JSON.
  return mediaManagerClient.post('/api/v1/sponsorship_report', body, { responseType: 'blob' });
}

type MutationFn = typeof postSponsorshipReport;

type TUsePostSponsorshipReportParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostSponsorshipReport({ config }: TUsePostSponsorshipReportParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: postSponsorshipReport,
    onError(error, variables, context) {
      config?.onError?.(error, variables, context);

      if (error.response?.status === 403) {
        addErrorNotification(t('No tienes permiso para exportar el informe de patrocinio'));
      } else {
        addErrorNotification(t('No se ha podido generar el excel de patrocinio'));
      }
      console.error(error);
    },
    onSuccess(data, variables, context) {
      config?.onSuccess?.(data, variables, context);
      addSuccessNotification(t('Se ha generado el excel de patrocinio con éxito'));
    },
  });
}
